import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import CallToAction from '../components/CallToAction';

const { FiChevronDown, FiChevronUp } = FiIcons;

const BOOK_COVER = "https://media-manager-c.questera.ai/greta-media/ae05bbc051fb17176d2c348bf77438a7a494626431a725f22ebe70ac4579d949c16512ea83019a888cf26e0e50680ce7/images/aW1hZ2UvanBlZw==/3614b5604f8ec9fc0b13dbae720eb360.jpg";

const excerpts = [
  {
    chapter: "Introduction",
    title: "The Language No One Taught You to Read",
    text: `We grew up watching love arrive in grand packages. The bouquet of roses delivered to the door. The airport run where he catches her just before she boards. The rooftop dinner with candles and a ring. These images taught us that romance is an event — something that happens to you, something you can photograph and show the world.\n\nBut what if the most romantic thing he ever does is simply stay? What if it is the coffee made exactly as you like it, without a word said? The way he slows the car when you are nervous, or sits beside you in silence when the world has been too loud?\n\nThis book is an invitation to look again. To see what has always been there, in the spaces between the grand gestures.`,
  },
  {
    chapter: "Chapter 2",
    title: "The Memory He Keeps of You",
    text: `He remembers the name of your difficult colleague. He knows exactly how you take your coffee — not because you told him once, but because he has been paying attention every single morning.\n\nThis is not a small thing. In a world where attention is the scarcest resource, the fact that he stores these details about you — your preferences, your fears, your small joys — is evidence of something profound. It is evidence that you matter to him in a way that goes beyond what he is able to say.\n\nBeing truly remembered is one of the most intimate experiences a person can have. And he is doing it, quietly, every day.`,
  },
  {
    chapter: "Chapter 3",
    title: "He Fixes Things You Did Not Ask Him To",
    text: `There is a particular kind of love that expresses itself through tools and effort. He notices the drawer that sticks. He re-hangs the frame that has been slightly crooked for months. He does not announce it. He does not wait for applause.\n\nThis is not practicality. It is attention. It is the quiet accumulation of care — the proof that he is paying attention to your world, and that he considers it his responsibility to make it better.\n\nWhen a man fixes things in your life without being asked, he is saying something he may never say out loud: your comfort matters to me. Your peace is my project. I am here.`,
  },
  {
    chapter: "Chapter 6",
    title: "Presence Is the Most Underrated Gift",
    text: `He does not always know what to say. And sometimes, he says nothing at all. He sits beside you. He stays. In a world that rewards busyness and performance, the choice to simply be present — unhurried, undistracted, fully there — is an act of profound generosity.\n\nWe have been taught to want words. We want to hear "I love you" spoken like a declaration. But there is a man who shows you every single day, in the way he does not leave when things are hard, in the way he turns toward you instead of away.\n\nHis silence is not absence. It is a different kind of language. And once you learn to read it, you will never feel unloved again.`,
  },
  {
    chapter: "Chapter 8",
    title: "The Man Who Stays",
    text: `Of all the signs explored in this book, this is the one that matters most. Not the flowers. Not the dinners. Not the surprises. The staying.\n\nIn a world full of exits — easier options, digital distractions, the constant temptation of novelty — the man who chooses to stay is performing an act of love so deep it rarely gets named. He is saying, with every ordinary day he shows up: I have seen all of you. I am not going anywhere.\n\nIf you have a man like this, you have something most people spend their lives searching for. This book was written to help you see it — and to never take it for granted again.`,
  },
];

const ReadExcerptPage = () => {
  const [openIndex, setOpenIndex] = useState(0);

  return (
    <main className="pt-24">
      <section className="py-16 bg-stone-900 text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-2 gap-12 items-center">
          <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }}>
            <span className="text-brand-400 font-semibold tracking-widest uppercase text-sm mb-4 block">Read Inside</span>
            <h1 className="text-4xl md:text-5xl font-serif font-bold mb-6">Sample Chapters</h1>
            <p className="text-stone-300 text-lg leading-relaxed">
              A glimpse into the writing style, emotional depth, and transformative ideas of <em>The Silent Language of Love</em>.
            </p>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2 }}
            className="flex justify-center">
            <img src={BOOK_COVER} alt="Book Cover"
              className="w-48 md:w-64 rounded-xl shadow-2xl object-cover aspect-[2/3]" />
          </motion.div>
        </div>
      </section>

      <section className="py-20 bg-stone-50">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <div className="space-y-4">
            {excerpts.map((excerpt, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 15 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                className="bg-white rounded-2xl border border-stone-100 overflow-hidden shadow-sm">
                <button onClick={() => setOpenIndex(openIndex === i ? null : i)}
                  className="w-full px-8 py-6 flex justify-between items-center text-left hover:bg-stone-50 transition-colors">
                  <div>
                    <span className="text-xs font-bold tracking-widest uppercase text-brand-500 block mb-1">{excerpt.chapter}</span>
                    <h3 className="text-lg md:text-xl font-serif font-bold text-stone-900">{excerpt.title}</h3>
                  </div>
                  <SafeIcon icon={openIndex === i ? FiChevronUp : FiChevronDown} className="w-5 h-5 text-stone-400 flex-shrink-0 ml-4" />
                </button>
                <AnimatePresence>
                  {openIndex === i && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3 }} className="overflow-hidden">
                      <div className="px-8 pb-8 border-t border-stone-100">
                        <div className="pt-6 space-y-4">
                          {excerpt.text.split('\n\n').map((para, j) => (
                            <p key={j} className="text-stone-600 leading-relaxed text-lg font-serif italic">{para}</p>
                          ))}
                        </div>
                        <div className="mt-8 pt-6 border-t border-stone-100 flex items-center justify-between">
                          <p className="text-sm text-stone-400">— From <em>The Silent Language of Love</em></p>
                          <a href="https://www.amazon.com/dp/B0GHP512XW" target="_blank" rel="noopener noreferrer"
                            className="text-sm font-semibold text-brand-600 hover:text-brand-800 transition-colors">
                            Read the full book →
                          </a>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <div id="get-copy"><CallToAction /></div>
    </main>
  );
};

export default ReadExcerptPage;